import React, { useState, useEffect } from "react";
import { baseurl } from "../../baseurl";
import axios from "axios";
import { FiPhone, FiMail, FiMapPin, FiHash, FiCalendar, FiFileText } from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS — "Elegance" design
   Left gold stripe + clean white body + serif-style headings
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

.it4__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it4__wrap {
  font-family: 'DM Sans', sans-serif;
  background: #dde8e8;
  min-height: 100vh;
 
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── A4 paper ── */
.it4__paper {
  width: 210mm;
  min-height: 297mm;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
}

/* ═══════════════════════════════
   LEFT STRIPE
═══════════════════════════════ */
.it4__stripe {
  width: 56px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #0a3d3e 0%, #0a5556 40%, #1a6b5a 80%, #155c4a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 32px 0 24px;
  position: relative;
}

/* vertical text */
.it4__stripe-word {
  font-family: 'Playfair Display', serif;
  font-size: 11px; font-weight: 700; letter-spacing: 0.22em;
  text-transform: uppercase; color: rgba(255,255,255,0.35);
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}
.it4__stripe-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #FFD166;
  box-shadow: 0 0 10px rgba(255,209,102,0.6);
}
.it4__stripe-line {
  flex: 1;
  width: 1px;
  background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,209,102,0.25), rgba(255,255,255,0.08));
  margin: 14px 0;
}

/* ═══════════════════════════════
   MAIN BODY (right of stripe)
═══════════════════════════════ */
.it4__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* ── HEADER ── */
.it4__header {
  padding: 36px 40px 28px;
  border-bottom: 2px solid #f5f0e8;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.it4__company-name {
  font-family: 'Playfair Display', serif;
  font-size: 26px; font-weight: 800; color: #0a3d3e;
  letter-spacing: -0.01em; line-height: 1.1; margin-bottom: 10px;
}
.it4__seller-row {
  display: flex; align-items: center; gap: 7px;
  font-size: 12px; color: #7a9090; margin-bottom: 5px;
}
.it4__seller-row svg { color: #0a5556; flex-shrink: 0; }
.it4__gstin-tag {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 6px; margin-top: 4px;
  background: #f5f0e8; border: 1px solid #e8d8b0;
  font-size: 10.5px; font-weight: 700; color: #8a6500;
  letter-spacing: 0.04em;
}

/* invoice title block */
.it4__title-block { text-align: right; }
.it4__inv-label {
  font-family: 'Playfair Display', serif;
  font-size: 38px; font-weight: 800; color: #0a3d3e;
  letter-spacing: 0.08em; line-height: 1; margin-bottom: 12px;
}
.it4__inv-label span {
  color: #FFD166;
}
.it4__meta-table { display: flex; flex-direction: column; gap: 6px; }
.it4__meta-row { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.it4__meta-lbl {
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: #9ababa;
}
.it4__meta-val {
  font-size: 13px; font-weight: 700; color: #0f2a2a;
  font-family: 'DM Sans', sans-serif; min-width: 100px; text-align: right;
}
/* status */
.it4__status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; border-radius: 50px;
  font-size: 11px; font-weight: 700;
}
.it4__status--paid    { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
.it4__status--unpaid  { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.it4__status--partial { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
.it4__status--pending { background: #f0f9ff; color: #075985; border: 1px solid #7dd3fc; }
.it4__status-dot { width: 6px; height: 6px; border-radius: 50%; }

/* ── BILLING GRID ── */
.it4__billing {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 0;
  border-bottom: 2px solid #f5f0e8;
}
.it4__bill-cell {
  padding: 22px 40px;
}
.it4__bill-cell + .it4__bill-cell {
  border-left: 1px solid #f5f0e8;
}
.it4__cell-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #FFD166; background: #0a5556; display: inline-block;
  padding: 3px 9px; border-radius: 4px; margin-bottom: 12px;
}
.it4__client-name {
  font-family: 'Playfair Display', serif;
  font-size: 16px; font-weight: 700; color: #0f2a2a; margin-bottom: 8px;
}
.it4__client-row {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 12px; color: #7a9090; margin-bottom: 5px; line-height: 1.4;
}
.it4__client-row svg { color: #0a5556; flex-shrink: 0; margin-top: 1px; }
.it4__gstin-sm {
  display: inline-block; padding: 2px 8px; border-radius: 5px;
  font-size: 10.5px; font-weight: 700; background: #f5f0e8;
  color: #8a6500; border: 1px solid #e8d8b0; margin-top: 3px;
}

/* detail rows in billing cell */
.it4__det-row {
  display: flex; align-items: center; gap: 10px; padding: 8px 0;
  border-bottom: 1px dashed #eef0f0;
}
.it4__det-row:last-child { border-bottom: none; }
.it4__det-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: #f5fbfb; border: 1px solid rgba(10,85,86,0.10);
  display: flex; align-items: center; justify-content: center; color: #0a5556;
}
.it4__det-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9ababa; margin-bottom: 1px; }
.it4__det-val { font-size: 13px; font-weight: 700; color: #0f2a2a; }

/* ── ITEMS TABLE ── */
.it4__table-wrap { padding: 26px 40px; }
.it4__table-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 12px;
}
.it4__table { width: 100%; border-collapse: collapse; }

.it4__table thead tr {
  border-top: 2px solid #0a5556;
  border-bottom: 2px solid #0a5556;
}
.it4__table thead th {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  color: #0a5556; padding: 10px 12px; text-align: left; background: #f5fbfb;
}
.it4__table thead th:first-child { padding-left: 0; }
.it4__table thead th.th-r { text-align: right; padding-right: 0; }
.it4__table thead th.th-c { text-align: center; }

.it4__table tbody tr { border-bottom: 1px dashed #eef0f0; transition: background 0.12s; }
.it4__table tbody tr:last-child { border-bottom: none; }
.it4__table tbody tr:hover { background: #fafcfc; }
.it4__table tbody td { padding: 12px 12px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it4__table tbody td:first-child { padding-left: 0; }
.it4__table tbody td.td-r { text-align: right; padding-right: 0; color: #5a8080; font-weight: 600; }
.it4__table tbody td.td-c { text-align: center; font-weight: 700; }
.it4__table tbody td.td-total { text-align: right; padding-right: 0; }

.it4__item-name { font-size: 13px; font-weight: 700; color: #0f2a2a; margin-bottom: 3px; }
.it4__hsn-tag {
  display: inline-block; padding: 1px 7px; border-radius: 4px;
  font-size: 10px; font-weight: 700; color: #7a9090;
  background: #f0f4f4; border: 1px dashed #d0dada;
}
.it4__tax-tag {
  display: inline-block; padding: 1px 7px; border-radius: 4px;
  font-size: 10px; font-weight: 700; color: #0a5556;
  background: rgba(10,85,86,0.07); margin-left: 4px;
}
.it4__total-val {
  font-family: 'Playfair Display', serif;
  font-size: 14px; font-weight: 700; color: #0a3d3e;
}

/* ── FOOTER ── */
.it4__footer {
  display: flex; align-items: flex-start; gap: 0;
  border-top: 2px solid #f5f0e8;
  margin-top: auto;
}
.it4__notes-area {
  flex: 1; padding: 20px 40px;
  border-right: 1px solid #f5f0e8;
}
.it4__notes-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 8px;
}
.it4__notes-txt { font-size: 12.5px; color: #7a9090; line-height: 1.6; }

/* summary */
.it4__summary { width: 240px; flex-shrink: 0; }
.it4__sum-row {
  display: flex; justify-content: space-between; padding: 10px 24px;
  border-bottom: 1px solid #f5f0e8;
  font-size: 12.5px;
}
.it4__sum-lbl { color: #7a9090; font-weight: 500; }
.it4__sum-val { font-weight: 700; color: #0f2a2a; }
.it4__grand {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 24px;
  background: #0a3d3e;
}
.it4__grand-lbl {
  font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  color: rgba(255,255,255,0.55);
}
.it4__grand-val {
  font-family: 'Playfair Display', serif;
  font-size: 20px; font-weight: 800; color: #FFD166;
}

/* ── BOTTOM ── */
.it4__bottom {
  padding: 12px 40px;
  background: #f9fafa;
  border-top: 1px solid #eef0f0;
  display: flex; align-items: center; gap: 7px;
}
.it4__bottom svg { color: #0a5556; }
.it4__bottom-txt {
  font-size: 10.5px; font-weight: 600; color: #9ababa; letter-spacing: 0.05em;
}

/* print */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it4__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it4__paper { width: 210mm !important; min-height: 297mm !important; box-shadow: none !important; margin: 0 !important; }
}
@media (max-width: 640px) {
  .it4__paper { flex-direction: column; }
  .it4__stripe { width: 100%; height: 8px; flex-direction: row; padding: 0 20px; }
  .it4__stripe-word, .it4__stripe-line { display: none; }
  .it4__billing { grid-template-columns: 1fr; }
  .it4__footer { flex-direction: column; }
  .it4__summary { width: 100%; }
}
`;

/* ─── helpers ─── */
const num   = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmtIN = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const init  = (name = "") => name.trim()[0]?.toUpperCase() || "?";

const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it4__status--paid",    dot: "#10b981" };
  if (s === "unpaid")  return { cls: "it4__status--unpaid",  dot: "#ef4444" };
  if (s === "partial") return { cls: "it4__status--partial", dot: "#f59e0b" };
  return                      { cls: "it4__status--pending", dot: "#38bdf8" };
};

/* ═══════════════════════ COMPONENT ═══════════════════════ */
const InvoiceFour = ({ invoiceData }) => {
  const [sellerInfo, setSellerInfo] = useState(null);
  const token = localStorage.getItem("auth_token");

  console.log("invoiceData", invoiceData);

  /* ── FETCH BUSINESS INFO ── */
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);
        if (response.data.success) {
          setSellerInfo(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching business info for seller:", error);
      }
    };
    fetchBusinessInfo();
  }, [token]);

  /* ── DEFAULT DATA ── */
  const defaultData = {
    invoice_number: "INV-000",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "N/A",
    payment_status: "Pending",
    notes: "Thank you for your business.",
    seller: { name: "N/A", gstin: "N/A", phone: "N/A", state: "N/A", email: "", zipcode: "" },
    customer: { name: "Client Name", email: "client@example.com", gstin: "N/A", phone: "N/A", state: "N/A", address: "Client Address Here" },
    items: [],
    gst_breakdown: { CGST: 0, SGST: 0 },
    subtotal: 0,
    taxable_amount: 0,
    grand_total: 0,
  };

  /* ── FINAL RESOLVED DATA ── */
  const resolvedSeller =
    invoiceData?.invoice_data?.seller ||
    (sellerInfo ? {
      name:   sellerInfo.business_name,
      gstin:  sellerInfo.gst || "N/A",
      phone:  sellerInfo.whatsapp_number,
      state:  sellerInfo.state,
      city:   sellerInfo.city,
      zipcode: sellerInfo.zipcode,
      email:  sellerInfo.contact_email || sellerInfo.email,
    } : defaultData.seller);

  const resolved = invoiceData?.invoice_data
    ? {
        invoice_number:  invoiceData.invoice_number || defaultData.invoice_number,
        invoice_date:    invoiceData.invoice_data.invoiceDate || defaultData.invoice_date,
        due_date:        invoiceData.invoice_data.dueDate,
        payment_status:  invoiceData.invoice_data.paymentStatus,
        seller:          resolvedSeller,
        customer:        invoiceData.invoice_data.customer || defaultData.customer,
        items:           invoiceData.invoice_data.items || [],
        gst_breakdown:   invoiceData.invoice_data.gstBreakdown || { CGST: 0, SGST: 0 },
        subtotal:        num(invoiceData.invoice_data.itemSubtotal),
        taxable_amount:  num(invoiceData.invoice_data.taxableAmount),
        grand_total:     num(invoiceData.grand_total ?? invoiceData.invoice_data.grandTotal),
        notes:           invoiceData.invoice_data.notes || defaultData.notes,
      }
    : { ...defaultData, seller: resolvedSeller };

  const {
    invoice_number, invoice_date, payment_status,
    seller, customer, items, gst_breakdown,
    subtotal, grand_total, notes,
  } = resolved;

  const si = getStatusInfo(payment_status);

  return (
    <>
      <style>{CSS}</style>
      <div className="it4__wrap">
        <div className="it4__paper">

          {/* ── LEFT STRIPE ── */}
          <div className="it4__stripe">
            <div className="it4__stripe-dot" />
            <div className="it4__stripe-line" />
            <div className="it4__stripe-word">Tax Invoice</div>
            <div className="it4__stripe-line" />
            <div className="it4__stripe-dot" />
          </div>

          {/* ── BODY ── */}
          <div className="it4__body">

            {/* HEADER */}
            <header className="it4__header">
              <div>
                <div className="it4__company-name">{seller.name}</div>
                {(seller.city || seller.state) && (
                  <div className="it4__seller-row"><FiMapPin size={12} />{seller.city && `${seller.city}, `}{seller.state} {seller.zipcode}</div>
                )}
                {seller.phone && <div className="it4__seller-row"><FiPhone size={12} />{seller.phone}</div>}
                {seller.email && <div className="it4__seller-row"><FiMail size={12} />{seller.email}</div>}
                {seller.gstin !== "N/A" && <div className="it4__gstin-tag">GSTIN: {seller.gstin}</div>}
              </div>

              <div className="it4__title-block">
                <div className="it4__inv-label">INVO<span>ICE</span></div>
                <div className="it4__meta-table">
                  <div className="it4__meta-row">
                    <span className="it4__meta-lbl">Number</span>
                    <span className="it4__meta-val">{invoice_number}</span>
                  </div>
                  <div className="it4__meta-row">
                    <span className="it4__meta-lbl">Date</span>
                    <span className="it4__meta-val">{invoice_date}</span>
                  </div>
                  <div className="it4__meta-row">
                    <span className="it4__meta-lbl">Status</span>
                    <span className="it4__meta-val">
                      <span className={`it4__status ${si.cls}`}>
                        <span className="it4__status-dot" style={{ background: si.dot }} />
                        {payment_status}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </header>

            {/* BILLING GRID */}
            <div className="it4__billing">
              <div className="it4__bill-cell">
                <div className="it4__cell-lbl">Bill To</div>
                <div className="it4__client-name">{customer.name}</div>
                {customer.address && <div className="it4__client-row"><FiMapPin size={11} />{customer.address}</div>}
                {customer.phone   && <div className="it4__client-row"><FiPhone  size={11} />{customer.phone}</div>}
                {customer.email   && <div className="it4__client-row"><FiMail   size={11} />{customer.email}</div>}
                {customer.gstin !== "N/A" && customer.gstin && (
                  <span className="it4__gstin-sm">GSTIN: {customer.gstin}</span>
                )}
              </div>
              <div className="it4__bill-cell">
                <div className="it4__cell-lbl">Details</div>
                <div className="it4__det-row">
                  <div className="it4__det-icon"><FiHash size={13} /></div>
                  <div>
                    <div className="it4__det-lbl">Invoice No</div>
                    <div className="it4__det-val">{invoice_number}</div>
                  </div>
                </div>
                <div className="it4__det-row">
                  <div className="it4__det-icon"><FiCalendar size={13} /></div>
                  <div>
                    <div className="it4__det-lbl">Invoice Date</div>
                    <div className="it4__det-val">{invoice_date}</div>
                  </div>
                </div>
                <div className="it4__det-row">
                  <div className="it4__det-icon"><FiCalendar size={13} /></div>
                  <div>
                    <div className="it4__det-lbl">Due Date</div>
                    <div className="it4__det-val">{resolved.due_date || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div className="it4__table-wrap">
              <div className="it4__table-lbl">Line Items</div>
              <table className="it4__table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="th-c">HSN</th>
                    <th className="th-c">Qty</th>
                    <th className="th-r">Rate</th>
                    <th className="th-r">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.map((item, i) => {
                    const lineTotal = num(item.qty) * num(item.rate) * (1 + num(item.tax) / 100);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="it4__item-name">{item.description}</div>
                          <span className="it4__hsn-tag">{item.hsn || "—"}</span>
                          <span className="it4__tax-tag">{item.tax}% GST</span>
                        </td>
                        <td className="td-c">{item.hsn || "—"}</td>
                        <td className="td-c">{item.qty}</td>
                        <td className="td-r">₹{fmtIN(item.rate)}</td>
                        <td className="td-total">
                          <span className="it4__total-val">₹{fmtIN(lineTotal)}</span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="5" style={{ textAlign: "center", padding: "32px", color: "#9ababa", fontWeight: 700 }}>No items found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* FOOTER */}
            <div className="it4__footer">
              <div className="it4__notes-area">
                <div className="it4__notes-lbl">Notes & Remarks</div>
                <div className="it4__notes-txt">{notes || "Thank you for your business."}</div>
              </div>
              <div className="it4__summary">
                <div className="it4__sum-row">
                  <span className="it4__sum-lbl">Subtotal</span>
                  <span className="it4__sum-val">₹{fmtIN(subtotal)}</span>
                </div>
                <div className="it4__sum-row">
                  <span className="it4__sum-lbl">CGST</span>
                  <span className="it4__sum-val">₹{fmtIN(gst_breakdown.CGST)}</span>
                </div>
                <div className="it4__sum-row">
                  <span className="it4__sum-lbl">SGST</span>
                  <span className="it4__sum-val">₹{fmtIN(gst_breakdown.SGST)}</span>
                </div>
                <div className="it4__grand">
                  <span className="it4__grand-lbl">Amount Due</span>
                  <span className="it4__grand-val">₹{fmtIN(grand_total)}</span>
                </div>
              </div>
            </div>

            {/* BOTTOM */}
            <div className="it4__bottom">
              <FiFileText size={12} />
              <span className="it4__bottom-txt">This is a computer generated invoice.</span>
            </div>

          </div>{/* end body */}
        </div>
      </div>
    </>
  );
};

export default InvoiceFour;
